// @givmo/app-charity — read-only profile view for everyone who is not a Global Admin (CPA-WEB6).
//
// A Member, or an Owner of an app, can SEE the organization profile but not edit it — profile writes are
// gated on `charity_profile:write` (the org-level role only; no app's catalog carries it) and we FAIL
// CLOSED: no edit controls, no file input, just the values + an honest "ask a Global Admin" note. The
// note names the org-level role as the console does, never "Owner" (R6; CPA-WEB94). Mirrors the payment
// page's masked view. The logo is shown as a static avatar (no upload button). The verification domain
// guard (<DomainGuard>) is rendered by the parent for everyone.

import { Avatar, Card, CardBody, CardHeader, Icon, testIdProp } from '@givmo/design-system';
import { Lock } from 'lucide-react';
import type { CharityProfileOut } from '@givmo/api-client';
import { websiteFromDomain } from './profile-schemas';

const PREFIX = 'profile-readonly';

export interface ProfileReadonlyViewProps {
  profile: CharityProfileOut;
}

function DisplayRow({
  label,
  value,
  testId,
  href,
}: {
  label: string;
  value: string;
  testId: string;
  href?: string;
}) {
  const display = value.trim().length > 0 ? value : '—';
  return (
    <div className="flex flex-col gap-2">
      <span className="font-sans text-s12 font-w600 tracking-givmo text-text-tertiary">
        {label}
      </span>
      {href ? (
        <a
          href={href}
          className="font-sans text-s14 text-brand-700 hover:underline"
          {...testIdProp(testId)}
        >
          {display}
        </a>
      ) : (
        <span className="font-sans text-s14 text-text-strong" {...testIdProp(testId)}>
          {display}
        </span>
      )}
    </div>
  );
}

export function ProfileReadonlyView({ profile }: ProfileReadonlyViewProps) {
  // The website IS the organization domain (D-CP-73): derive the same `https://{domain}` both apps
  // render, rather than reading a stored URL the profile no longer carries.
  const website = websiteFromDomain(profile.organization_domain);
  const websiteHref = website.length > 0 ? website : undefined;
  const email = profile.contact_email?.trim() ?? '';
  const phone = profile.contact_phone?.trim() ?? '';
  return (
    <>
      <div
        className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-14"
        {...testIdProp(`${PREFIX}-note`)}
      >
        <Icon icon={Lock} size={18} className="mt-1 shrink-0 text-text-tertiary" aria-hidden />
        <p className="text-s13 text-text-secondary">
          Only a Global Admin can edit these details or the logo. You can view them below — ask a
          Global Admin to make changes.
        </p>
      </div>

      <Card testId={`${PREFIX}-card`}>
        <CardHeader title="Organization details" />
        <CardBody className="flex flex-col gap-16">
          <div className="flex items-center gap-16">
            <Avatar
              src={profile.logo_url ?? null}
              name={profile.name}
              size="lg"
              testId={`${PREFIX}-logo`}
            />
            <div className="min-w-0">
              <div className="truncate font-sans text-s16 font-w600 text-text-strong">
                {profile.name}
              </div>
            </div>
          </div>

          <DisplayRow label="Mission" value={profile.mission ?? ''} testId={`${PREFIX}-mission`} />
          <DisplayRow
            label="Website"
            value={website}
            href={websiteHref}
            testId={`${PREFIX}-website`}
          />
          <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
            <DisplayRow
              label="Contact email"
              value={email}
              href={email ? `mailto:${email}` : undefined}
              testId={`${PREFIX}-contact-email`}
            />
            <DisplayRow
              label="Contact phone"
              value={phone}
              href={phone ? `tel:${phone}` : undefined}
              testId={`${PREFIX}-contact-phone`}
            />
          </div>
        </CardBody>
      </Card>
    </>
  );
}
